"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useTranslations } from "next-intl";
import Link from "next/link";

export type PaginationData = { page: number; total: number; limit: number };
export type PaginationProps<T extends PaginationData> = {
  data: T;
  onPageChange?: (page: number) => void;
  getPageHref?: (page: number) => string;
  isLoading?: boolean;
};

export function Pagination<T extends PaginationData>({
  data,
  onPageChange,
  getPageHref,
  isLoading = false,
}: PaginationProps<T>) {
  const t = useTranslations();
  const lastPage = Math.max(1, Math.ceil(data.total / data.limit));
  const controls = [
    {
      page: 1,
      key: "first",
      icon: "ChevronLeftDouble",
      enabled: data.page > 1,
    },
    {
      page: data.page - 1,
      key: "previous",
      icon: "ChevronLeft",
      enabled: data.page > 1,
    },
    {
      page: data.page + 1,
      key: "next",
      icon: "ChevronRight",
      enabled: data.page < lastPage,
    },
    {
      page: lastPage,
      key: "last",
      icon: "ChevronRightDouble",
      enabled: data.page < lastPage,
    },
  ] as const;
  const renderControl = (control: (typeof controls)[number]) => {
    const label = t(`common.pagination.${control.key}`);
    const icon = <Icon icon={control.icon} aria-hidden />;
    const enabled = control.enabled && !isLoading;
    if (getPageHref && enabled) {
      return (
        <Button key={control.key} variant="outline" asChild>
          <Link href={getPageHref(control.page)} aria-label={label}>
            {icon}
          </Link>
        </Button>
      );
    }
    return (
      <Button
        key={control.key}
        variant="outline"
        disabled={!enabled}
        onClick={() => onPageChange?.(control.page)}
        aria-label={label}
      >
        {icon}
      </Button>
    );
  };
  return (
    <nav
      aria-label={t("common.pagination.page", { current: data.page })}
      className="flex items-center justify-center gap-2 py-6"
    >
      {controls.slice(0, 2).map(renderControl)}
      <div className="px-4" aria-live="polite">
        {t("common.pagination.page", { current: data.page })}
      </div>
      {controls.slice(2).map(renderControl)}
    </nav>
  );
}

Pagination.displayName = "Pagination";
